/**
 * TGC Gear — content guard.
 *
 * Blocks the right-click menu, dragging, text selection, copy and cut, and the
 * keyboard shortcuts a page is actually allowed to intercept.
 *
 * WHAT THIS DOES NOT DO, stated plainly so nobody relies on it:
 *
 *   It cannot stop DevTools. DevTools is a browser feature outside the page's
 *   sandbox. F12, Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C, Cmd+Option+I and the
 *   rest are handled by the browser BEFORE the page is told about them, so
 *   preventDefault here does not stop them in Chrome, Edge, Firefox or Safari.
 *   Measured, so nobody has to wonder whether this file is simply wrong: a real
 *   Cmd+Option+I dispatched at the browser DOES reach this handler and preventDefault
 *   IS applied to it — and DevTools opens regardless. Same for Cmd+Option+J,
 *   Cmd+Option+C, Cmd+Option+U and F12. preventDefault cancels the PAGE's default
 *   action for a key, not the browser's own command binding, and a page has no
 *   access to the second one. The menu route (⋮ → More tools → Developer tools, or
 *   Safari's Develop menu) is not reachable either, and neither is someone who
 *   opened DevTools before navigating here. This is friction, never a lock.
 *
 *   It cannot protect an image or a video. If the browser rendered it, the file
 *   is already on the visitor's machine, and its CDN URL is in the HTML: one
 *   `curl` fetches it with no browser and no JavaScript involved. Measured on
 *   this store: HTTP 200, 53,967 bytes, JPEG 800x800. Nothing in a page can
 *   change that. This is a deterrent against casual copying and nothing more.
 *
 *   It cannot block the Windows / Super key or Print Screen. Those never reach
 *   the page; the operating system takes them. The same is true of a screen
 *   reader's own commands, which is the one place this limitation helps: VoiceOver
 *   uses Ctrl+Option combinations that this handler cancels on paper, and they
 *   keep working because the OS claims them first.
 *
 * WHAT IT DELIBERATELY LEAVES ALONE, because breaking it would cost sales or
 * shut people out without protecting a single image:
 *
 *   form fields — a shopper has to be able to type, select and paste a discount
 *   code, an email address or a search term. EDITABLE below is the exemption.
 *   zoom — Ctrl/Cmd with +, - or 0 is how a partially sighted shopper reads the
 *   page at all.
 *   paste — protects nothing, and is needed in those same fields.
 */

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';

/** @param {EventTarget | null} target */
function editable(target) {
  return target instanceof Element ? Boolean(target.closest(EDITABLE)) : false;
}

/**
 * Browser commands, cancelled wherever the caret is. Nobody presses Cmd+U inside
 * a search box, so exempting fields here only left a hole.
 *
 *   s  Ctrl+S / Cmd+S      save page
 *   u  Ctrl+U              view source (Windows, Linux)
 *   p  Ctrl+P / Cmd+P      print
 */
const BROWSER_COMBOS = new Set(['s', 'u', 'p']);

/**
 * Editing commands. These are cancelled outside form fields only — a shopper has
 * to be able to copy an order number and select a discount code they mistyped.
 * Paste is deliberately absent: it protects nothing and is needed in those fields.
 */
const EDIT_COMBOS = new Set(['c', 'x', 'a']);

/** Zoom, by key and by physical key. It is how some people read the page at all. */
const ZOOM_KEYS = new Set(['+', '-', '=', '0']);
const ZOOM_CODES = new Set([
  'equal',
  'minus',
  'digit0',
  'numpadadd',
  'numpadsubtract',
  'numpad0',
]);

/**
 * Function keys the debugger uses, on both spellings of the event. F11 is left
 * out: it is fullscreen, and taking that away from someone is worse than the one
 * browser that also puts an inspector on it.
 *
 *   f12  Chrome, Edge, Firefox, Opera — open developer tools
 *   f8   Chrome, Edge, Firefox        — pause / resume the debugger
 *   f9   Firefox, Chrome              — step
 *   f10  Chrome, Edge, Firefox        — step over; also Shift+F10 = context menu
 */
const FUNCTION_KEYS = new Set(['f12', 'f8', 'f9', 'f10']);

/**
 * The letter behind a keystroke, from `event.code`, which reports the physical
 * key rather than what it produced. Two cases need it and neither is unusual:
 *
 *   macOS with Option held remaps the character — Option+I can arrive as a dead
 *   key rather than "i", so a rule written against `event.key` alone misses
 *   Cmd+Option+I, the Safari and Chrome web-inspector shortcut on every Mac.
 *
 *   a non-Latin layout reports the letter of that layout — Cmd+S on a Russian
 *   or Greek keyboard arrives as "ы" or "σ" while the physical key stays KeyS.
 *
 * @param {string} code lower-cased event.code
 */
function physicalChar(code) {
  if (/^key[a-z]$/.test(code)) return code.slice(3);
  if (/^digit\d$/.test(code)) return code.slice(5);
  return '';
}

class ContentGuard {
  #abort = new AbortController();

  constructor(options) {
    this.options = options;
    const { signal } = this.#abort;
    const on = (type, handler) => document.addEventListener(type, handler, { capture: true, signal });

    if (options.contextMenu) on('contextmenu', this.#block);
    if (options.drag) {
      on('dragstart', this.#block);
      on('drop', this.#block);
    }
    if (options.select) {
      on('selectstart', this.#block);
      on('copy', this.#block);
      on('cut', this.#block);
    }
    if (options.keys) on('keydown', this.#onKeydown);
  }

  destroy() {
    this.#abort.abort();
  }

  #block = (event) => {
    if (editable(event.target)) return;
    event.preventDefault();
  };

  #onKeydown = (event) => {
    const key = (event.key || '').toLowerCase();
    const code = (event.code || '').toLowerCase();
    const char = physicalChar(code);
    const mod = event.ctrlKey || event.metaKey;

    // Zoom stays, and it has to be checked first or the shapes below would take it.
    if (mod && (ZOOM_KEYS.has(key) || ZOOM_CODES.has(code))) return;

    // The developer-tools shapes below are cancelled wherever the caret happens to
    // be, form fields included: nobody types Cmd+Option+I into a search box, and
    // leaving a hole there was simply inconsistent. The copy, cut and select-all
    // exemption for fields is further down, where it matters.
    if (FUNCTION_KEYS.has(key) || FUNCTION_KEYS.has(code)) {
      event.preventDefault();
      return;
    }

    // Windows keyboards carry a dedicated Menu key, which opens the same context
    // menu the right button does. It also fires a contextmenu event, so this is
    // belt and braces — but Shift+F10 does the same job and is only caught above
    // because F10 happens to be in the set, so being explicit is worth the line.
    if (key === 'contextmenu') {
      event.preventDefault();
      return;
    }

    // Every developer-tools shortcut across the browsers lives in one of two
    // shapes, so both shapes are taken whole rather than kept as a list that
    // would go out of date:
    //
    //   Ctrl/Cmd + Shift + letter   Chrome and Edge I, J, C, P; Firefox I, K, C,
    //                               E, M, A, Q; Safari's console and inspector
    //   Ctrl/Cmd + Alt   + letter   the Mac spellings of all of the above, plus
    //                               Cmd+Option+U for view-source
    //
    // The collateral is small: page content almost never binds these, and the
    // combinations in here that are not developer tools (hard reload, private
    // window, restore tab) are browser-level anyway, so preventing them changes
    // nothing either way.
    // Any single character or function key, not letters alone: Windows and Linux
    // put developer tools on Ctrl+Shift+<letter> and Ctrl+<letter>, macOS on
    // Cmd+Option+<letter>, and widening past letters costs nothing while making
    // the rule true to its description. Everything extra it catches
    // (Ctrl+Shift+Tab, Ctrl+Shift+Delete, Ctrl+Shift+digit) is browser-level, so
    // cancelling it changes nothing either way. Zoom has already returned above.
    const combo = key.length === 1 || /^f\d{1,2}$/.test(key) || char !== '' || /^f\d{1,2}$/.test(code);
    if (mod && combo && (event.shiftKey || event.altKey)) {
      event.preventDefault();
      return;
    }

    // Save, view-source and print: browser commands, so no field exemption.
    if (mod && !event.shiftKey && !event.altKey && (BROWSER_COMBOS.has(key) || BROWSER_COMBOS.has(char))) {
      event.preventDefault();
      return;
    }

    // Copy, cut and select-all: a field is exempt, because a shopper types in it.
    if (editable(event.target)) return;
    if (mod && !event.shiftKey && !event.altKey && (EDIT_COMBOS.has(key) || EDIT_COMBOS.has(char))) {
      event.preventDefault();
    }
  };
}

const root = document.documentElement;
if (root.dataset.tgcGuard) {
  new ContentGuard({
    contextMenu: root.dataset.tgcGuardContext !== 'false',
    drag: root.dataset.tgcGuardDrag !== 'false',
    select: root.dataset.tgcGuardSelect !== 'false',
    keys: root.dataset.tgcGuardKeys !== 'false',
  });
}
